import { useEffect, useState, useCallback } from 'react';
import {
  Stack, Group, Title, Text, Button, Box, Paper, Alert, Loader, Checkbox,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api } from '../../wheel-api/client';
import { useKiosk } from '../../context/KioskContext/KioskContext';
import { OpcionesGrandes } from './OpcionesGrandes';
import { TecladoNumerico } from './TecladoNumerico';
import { CapturaFoto } from './CapturaFoto';
import { ForzarEntrega } from './ForzarEntrega';
import { BotonesImpresion } from './BotonesImpresion';
import { componerFotoCaravana } from '../../utils/impresion';
import { getDeviceId } from './device';
import { UI } from '../../theme/kioskTheme';

const DIAS = [
  { value: 'MIE', label: 'Miércoles' },
  { value: 'JUE', label: 'Jueves' },
  { value: 'VIE', label: 'Viernes' },
];

export function EntregaScreen() {
  const { attendee, reset } = useKiosk();
  const [estado, setEstado] = useState(null);
  const [numero, setNumero] = useState('');
  const [dias, setDias] = useState([]);
  const [sinFoto, setSinFoto] = useState(false);
  // Solo se usa en días de prueba: en el congreso la mitad la decide el reloj.
  const [mitadForzada, setMitadForzada] = useState(null);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [fotoUrl, setFotoUrl] = useState(null);
  // La hoja se compone apenas se sube la foto, no al tocar imprimir:
  // navigator.share necesita el gesto del usuario sin await previo.
  const [hoja, setHoja] = useState(null);
  const [pideFoto, setPideFoto] = useState(false);
  const [autorizando, setAutorizando] = useState(false);

  const cargarEstado = useCallback(() => {
    api.gemelosEstado()
      .then(setEstado)
      .catch(() => notifications.show({ message: 'No pudimos leer la bolsa activa', color: 'red' }));
  }, []);

  useEffect(cargarEstado, [cargarEstado]);

  async function entregar(forzado = null) {
    setEnviando(true);
    setError(null);
    try {
      const r = await api.gemelosEntregar({
        number: Number(numero),
        attendeeId: attendee._id,
        daysPresent: dias,
        sinFoto,
        // En un día de prueba la elige el selector; al forzar, el panel.
        mitadForzada: forzado?.mitad || mitadForzada,
        deviceId: getDeviceId(),
        ...(forzado ? {
          forzar: true,
          pinDireccion: forzado.pinDireccion,
          motivo: forzado.motivo,
        } : {}),
      });
      setExito(r);
      setPideFoto(Boolean(r.pideFoto));
      cargarEstado();
    } catch (err) {
      // Los avisos de §5.1 van en rojo y bloqueando, con el motivo textual
      // para que el operador se lo pueda explicar a quien tiene enfrente.
      // El objeto entero: trae `forzable` y `pideMitad`.
      setError(err.data || { message: 'No pudimos entregar la caravana.' });
    } finally {
      setEnviando(false);
    }
  }


  // El permiso de imagen se puede dar en el momento: la persona está parada
  // enfrente. Solo suma permiso, nunca lo saca.
  async function autorizar() {
    setAutorizando(true);
    try {
      await api.gemelosAutorizarImagen(exito.tagId);
      setSinFoto(false);
      setPideFoto(true);
    } catch (err) {
      notifications.show({
        message: err.data?.message || 'No pudimos registrar la autorización.',
        color: 'red',
      });
    } finally { setAutorizando(false); }
  }

  // ---- Confirmación ------------------------------------------------------
  if (exito) {
    return (
      // Mismo motivo que en CaravanaScreen: el bloque de foto crece y sin
      // scroll el boton de cierre se va abajo del borde de la tablet.
      <Stack style={{ flex: 1, minHeight: 0 }} gap={0} px="8%">
        <Stack align="center" gap="xl" pt="md"
               style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Title
          order={1} ta="center"
          c={exito.cerroPar ? 'forvet.7' : 'forvet.6'}
          style={{ fontSize: 'clamp(28px, 5vh, 52px)' }}
        >
          {exito.cerroPar ? '¡PAR CERRADO! AVISANDO A LOS DOS' : 'CARAVANA ENTREGADA'}
        </Title>

        <Paper withBorder radius="lg" p="xl" style={{ borderWidth: 3, borderColor: UI.accion }}>
          <Text ta="center" c="forvet.6" size="lg">Número</Text>
          <Text ta="center" fw={900} style={{ fontSize: 'clamp(48px, 9vh, 90px)', lineHeight: 1 }}>
            {String(exito.numero).padStart(3, '0')}
          </Text>
          <Text ta="center" c="forvet.6" size="lg" mt="xs">
            bolsa {exito.bolsa} · {exito.nombre}
          </Text>
        </Paper>

        {pideFoto ? (
          <Stack align="center" gap="xs">
            <CapturaFoto
              label="SACAR LA FOTO"
              subiendo={subiendoFoto}
              urlActual={fotoUrl}
              onSubir={async (blob) => {
                setSubiendoFoto(true);
                try {
                  const r = await api.gemelosSubirFoto(exito.tagId, blob);
                  setFotoUrl(r.url);
                  // Se compone desde el blob local: la URL de S3 ensuciaría el
                  // canvas y toBlob fallaría por CORS.
                  setHoja(await componerFotoCaravana({
                    blob, numero: exito.numero, nombre: exito.nombre,
                  }));
                } catch (err) {
                  notifications.show({
                    message: err.data?.message || 'No pudimos subir la foto. Probá de nuevo.',
                    color: 'red',
                  });
                } finally { setSubiendoFoto(false); }
              }}
            />
            {fotoUrl && (
              <Alert color="blue" radius="md" style={{ maxWidth: 620 }}>
                <Text fw={700} size="lg">
                  Imprimila y pegala en el muro con el número {String(exito.numero).padStart(3, '0')}
                </Text>
              </Alert>
            )}
            {fotoUrl && (
              <BotonesImpresion
                blob={hoja}
                nombreArchivo={`caravana-${String(exito.numero).padStart(3, '0')}.png`}
                titulo={`Caravana ${String(exito.numero).padStart(3, '0')}`}
              />
            )}
          </Stack>
        ) : (
          <Stack align="center" gap="sm" maw={640}>
            <Alert color="gray" radius="md">
              <Text fw={700}>No le vamos a sacar la foto</Text>
              <Text size="sm">
                {sinFoto
                  ? 'Marcaste que no quiere foto.'
                  : 'En la ficha no autorizó el uso de su imagen (el casillero viene destildado y es fácil pasarlo por alto).'}
                {' '}Sin foto, su gemelo se queda con una sola pista para reconocerla.
              </Text>
            </Alert>
            <Button variant="outline" size="sm" loading={autorizando} onClick={autorizar}>
              SI AHORA ACEPTA, AUTORIZAR Y SACAR LA FOTO
            </Button>
          </Stack>
        )}

        </Stack>

        <Group justify="center" py="md" style={{ flexShrink: 0 }}>
          <Button h={78} w={400} onClick={reset}>SIGUIENTE PERSONA</Button>
        </Group>
      </Stack>
    );
  }

  if (!estado) {
    return <Stack align="center" justify="center" style={{ flex: 1 }}><Loader size="xl" /></Stack>;
  }

  // ---- Fuera de horario de entrega ---------------------------------------
  if (!estado.abierto) {
    return (
      <Stack align="center" justify="center" style={{ flex: 1 }} gap="lg" px="10%">
        <Title order={2} ta="center" c="forvet.7">AHORA NO SE ENTREGAN CARAVANAS</Title>
        <Text size="xl" ta="center" c="forvet.6" maw={700}>{estado.motivo?.mensaje}</Text>
        <Button variant="outline" h={64} onClick={reset}>Volver</Button>
      </Stack>
    );
  }

  const listo = numero.length > 0 && Number(numero) > 0 && dias.length > 0;

  return (
    <Stack style={{ flex: 1, minHeight: 0 }} px="6%" gap="sm">
      {estado.prueba && (
        <Alert color="orange" radius="md" py={6}>
          <Text fw={700}>MODO PRUEBA — la ventana de bolsas está abierta a la fuerza.
          Apagá GEMELOS_VENTANA_LIBRE antes del congreso.</Text>
        </Alert>
      )}

      {/* La bolsa activa va arriba de todo y en grande: el operador no decide */}
      <Paper radius="md" p="sm" style={{ background: UI.accion }}>
        <Group justify="space-between">
          <Text c="#fff" fw={700} size="xl">
            BOLSA ACTIVA: {estado.bolsa}
            <Text span c="#fff" opacity={0.75} size="md" ml="sm">hasta {estado.hasta}</Text>
          </Text>
          <Text c="#fff" size="lg">Entregadas hoy: {estado.entregadasHoy}</Text>
        </Group>
      </Paper>


      {estado.prueba && (
        <Paper radius="md" p="xs" withBorder>
          <Group gap="sm" wrap="wrap">
            <Text fw={600} fz="var(--fs-small)">Día de prueba — elegí la mitad:</Text>
            <OpcionesGrandes
              options={[{ value: 'A', label: 'Mitad A' }, { value: 'B', label: 'Mitad B' }]}
              value={mitadForzada}
              onChange={setMitadForzada}
            />
          </Group>
        </Paper>
      )}

      <Text size="lg" c="forvet.6">
        Entregando a <Text span fw={700} c="forvet.8">{attendee.name}</Text>
      </Text>

      {/* overflow auto: con el panel de forzado abierto el contenido crece
          y sin esto empuja los botones de abajo fuera de la pantalla, que
          en una tablet deja al operador sin forma de cancelar. */}
      <Group align="flex-start" gap="xl"
             style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Stack gap="xs" style={{ flex: '0 0 300px' }}>
          <Text size="lg" fw={600} c="forvet.8">Número de la caravana</Text>
          <TecladoNumerico value={numero} onChange={v => { setNumero(v); setError(null); }} />
        </Stack>

        <Stack gap="md" style={{ flex: 1 }}>
          <Box>
            <Text size="lg" fw={600} c="forvet.8" mb="xs">¿Qué días venís?</Text>
            <OpcionesGrandes multiple options={DIAS} value={dias} onChange={setDias} />
          </Box>

          {attendee.consentImage === false ? (
            <Alert color="gray" radius="md">
              Esta persona no autorizó el uso de su imagen. Se juega igual, sin foto.
            </Alert>
          ) : (
            <Checkbox
              size="lg" checked={sinFoto}
              onChange={e => setSinFoto(e.currentTarget.checked)}
              label="No quiere foto — se juega igual"
            />
          )}

          <ForzarEntrega error={error} enviando={enviando}
                         onForzar={f => entregar(f)} />
        </Stack>
      </Group>

      <Group grow pb="sm">
        <Button variant="outline" h={72} onClick={reset}>CANCELAR</Button>
        <Button h={72} disabled={!listo} loading={enviando} onClick={() => entregar()}>ENTREGAR</Button>
      </Group>
    </Stack>
  );
}
